/**
 * Notification Preferences API Client
 *
 * Wraps GET/PUT /api/v1/notification-preferences. Per-user self-service:
 * the backend derives user_id/customer_id from the JWT, so the client
 * MUST NOT send them in the PUT body. Sending identity fields is a silent
 * no-op under the current sanitizer but is forbidden by contract (Phase 7).
 *
 * Pane-of-glass constraint: this endpoint only mutates
 * alarms.notification_preferences. It never touches alarm state.
 */

import api from './api';

export type MinSeverity = 'warning' | 'critical' | 'off';

export interface NotificationPreferences {
  user_id: string;
  customer_id: string;
  email_enabled: boolean;
  sms_enabled: boolean;
  min_severity: MinSeverity;
  /** UUIDs of sites in scope; null = all sites the user has access to. */
  site_scope: string[] | null;
  email_address: string | null;
  phone_number: string | null;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  /**
   * ISO timestamp when the current `phone_number` completed OTP verification
   * (issue #264). Cleared when phone_number changes; SMS toggle is gated on
   * this being non-null.
   */
  phone_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Fields the client is allowed to send in a PUT.
 *
 * Intentionally excludes:
 * - user_id, customer_id — derived from JWT by backend
 * - created_at, updated_at — server-managed timestamps
 */
export type NotificationPreferencesUpdate = Partial<{
  email_enabled: boolean;
  sms_enabled: boolean;
  min_severity: MinSeverity;
  site_scope: string[] | null;
  email_address: string | null;
  phone_number: string | null;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  /**
   * The SMS consent copy this page actually RENDERED. The server stamps its own
   * version and program name into the `sms_opt_in` audit row; this lets the row
   * also carry what the user saw, so a browser still running an older bundle,
   * or a web/backend tenant-name mismatch, is visible in the record. Audit
   * evidence only — never used to decide anything.
   */
  consent_rendered: { version: string; program_name: string };
}>;

const ROUTE = '/v1/notification-preferences';

/**
 * Backend error codes returned from the verify endpoints. Frontend switches
 * on these to render localised user messaging.
 */
export type VerificationErrorCode =
  | 'invalid_phone_number'
  | 'verification_rate_limited'
  | 'invalid_code_format'
  | 'no_active_code'
  | 'code_expired'
  | 'max_attempts_reached'
  | 'incorrect_code';

export interface IncorrectCodeResponse {
  error: 'incorrect_code';
  attempts_remaining: number;
}

export const notificationPrefsApi = {
  async get(): Promise<NotificationPreferences> {
    const res = await api.get<NotificationPreferences>(ROUTE);
    return res.data;
  },

  async update(
    update: NotificationPreferencesUpdate,
  ): Promise<NotificationPreferences> {
    const res = await api.put<NotificationPreferences>(ROUTE, update);
    return res.data;
  },

  /**
   * Issue an OTP verification code to a candidate phone number. Backend
   * normalises to E.164, hashes and stores the 6-digit code, sends over the
   * configured SMS transport, and resets attempt count. Rate-limited to 3
   * starts per phone per hour.
   *
   * ⚠️ `ok: true` means the CODE WAS ISSUED, not that a text was delivered.
   * The backend deliberately answers 200 even when the transport cannot send,
   * because the code is persisted and dev/test environments complete
   * verification by reading it from the DB (issue #745 item 1). Read
   * `sms_dispatched` to tell the two apart: `false` means the user will never
   * receive the message and must be told so.
   *
   * Optional because an older backend omits it; `undefined` is "unknown", and
   * only an explicit `false` should raise a warning.
   */
  async verifyPhoneStart(
    phone_number: string,
  ): Promise<{ ok: true; sms_dispatched?: boolean }> {
    const res = await api.post<{ ok: true; sms_dispatched?: boolean }>(
      `${ROUTE}/phone/verify-start`,
      { phone_number },
    );
    return res.data;
  },

  /**
   * Confirm a candidate code. On success, `phone_verified_at` is set to NOW()
   * and the active code is cleared. On 5 wrong submissions the code is
   * invalidated and the user must restart with `verifyPhoneStart`.
   */
  async verifyPhoneConfirm(code: string): Promise<{ ok: true }> {
    const res = await api.post<{ ok: true }>(`${ROUTE}/phone/verify-confirm`, {
      code,
    });
    return res.data;
  },
};
